import {
    Box,
    Button,
    Divider,
    Grid,
    InputBase,
    Modal,
    Radio,
    Slide,
    Typography,
    useMediaQuery,
} from '@mui/material';
import {
    IconWrapper,
    InputField,
    IStyles,
    PrimaryButton,
    pxToRem,
    pxTovW,
    SelectMenu,
    theme,
} from '@geneo2-web/shared-ui';
import { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { ProjectContentCard } from './ProjectContentCard';

const styles: IStyles = {
    root: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100vw',
        marginY: { xs: pxToRem(10), md: pxToRem(18), lg: pxTovW(20) },
        boxSizing: 'border-box',
    },
    container: {
        display: 'flex',
        justifyContent: 'center',
        gap: { xs: pxToRem(40), md: pxToRem(18), lg: pxTovW(20) },
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'center', md: 'flex-start' },
        width: { xs: '95%', md: 'unset' },
    },
    contentBox: {
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: pxToRem(15), md: pxToRem(15), lg: pxTovW(15) },
        backgroundColor: 'white',
        width: { xs: '95%', md: pxToRem(540), lg: pxTovW(597) },
        borderRadius: { xs: pxToRem(10), md: pxToRem(18), lg: pxTovW(20) },
        paddingX: { xs: pxToRem(10), md: pxToRem(24), lg: pxTovW(30) },
        paddingY: { xs: pxToRem(10), md: pxToRem(20), lg: pxTovW(23) },
        maxHeight: { xs: '85vh', md: '90vh' },
        overflowY: 'hidden',
        boxSizing: 'border-box',
    },
    classAndSectionContainer: {
        display: 'flex',
        flexDirection: { xs: 'column', md: 'column' },
        gap: { xs: pxToRem(15), md: pxToRem(21), lg: pxTovW(25) },
        width: '100%',
        flex: 1,
        minHeight: 0,
    },
    proceedButton: {
        width: { xs: '50%', md: pxToRem(150), lg: pxTovW(200) },
        height: { xs: pxToRem(35), md: 'unset' },
        fontWeight: 'bold',
        paddingY: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
    },
};


export interface IProjectMarksResource {
    serialNumber: string;
    taskName: string;
    contentName: string;
    duration: string;
}

interface IProps {
    popupOpen: boolean;
    individualClick: () => void;
    popupCloseHandler: () => void;
    proceedButtonData: { onClick: (marks: number) => void; disabled: boolean };
    groupName?: string;
    memberCount?: number;
    maxMarks?: number;
    /** Falls back to sample content when the caller has no resources to show. */
    resources?: IProjectMarksResource[];
}
export const ProjectGroupMarksDialog = (props: IProps) => {
    const {
        popupOpen,
        individualClick,
        popupCloseHandler,
        proceedButtonData,
        groupName,
        memberCount,
        maxMarks,
        resources,
    } = props;
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [groupMarks, setGroupMarks] = useState('');
    // No made-up fallback: the server rejects anything above the real max, so
    // guessing (e.g. 100) only turns a load failure into a confusing error.
    // Without a real max, marks entry is blocked and the teacher is told why.
    const hasMaxMarks = !!maxMarks && maxMarks > 0;
    const totalMarks = hasMaxMarks ? maxMarks : 0;

    /** Blank on every open: the field is for entering marks, not for showing them. */
    useEffect(() => {
        if (popupOpen) {
            setGroupMarks('');
        }
    }, [popupOpen]);

    const handleGroupMarksChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = event.target.value;

        if (!/^\d*$/.test(value)) return;

        if (value === '' || Number(value) <= totalMarks) {
            setGroupMarks(value);
        }
    };
    const projectContents = resources?.length ? resources : [];
    return (
        <Modal open={popupOpen} onClose={popupCloseHandler}>
            <Slide
                direction="up"
                in={popupOpen}
                mountOnEnter
                unmountOnExit
                timeout={500}
            >
                <Box sx={styles.root}>
                    <Box sx={styles.container}>
                        <Box sx={styles.contentBox}>
                            <Typography variant={'bodyText'} fontWeight="bold" textAlign={'center'}>
                                Give Marks
                            </Typography>
                            <Box sx={styles.classAndSectionContainer}>
                                <Box sx={{
                                    height: { xs: pxToRem(102), md: pxToRem(78), lg: pxTovW(119) },
                                    width: { xs: '100%', md: pxToRem(480), lg: pxTovW(537) },
                                    borderRadius: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
                                    backgroundColor: '#EFF4EF',
                                    border: '1px solid #DBDBDB',
                                    padding: {
                                        xs: `${pxToRem(12)} ${pxToRem(12)} ${pxToRem(12)} ${pxToRem(12)}`,
                                        md: pxToRem(14),
                                        lg: `${pxTovW(24)} ${pxTovW(17)}`
                                    },
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: { xs: pxToRem(10), md: pxToRem(21), lg: pxTovW(24) },
                                    boxSizing: 'border-box',
                                }}>
                                    <IconWrapper
                                        name="gil-group"
                                        parentFolder="icons"
                                        type="png"
                                        size="small"
                                        customSx={{
                                            height: {
                                                xs: pxToRem(46),
                                                md: pxToRem(34),
                                                lg: pxTovW(46),
                                            },
                                            width: {
                                                xs: pxToRem(46),
                                                md: pxToRem(34),
                                                lg: pxTovW(46),
                                            },
                                            cursor: 'pointer',
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            width: "100%",
                                            gap: { xs: pxToRem(12), md: pxToRem(18), lg: pxTovW(20) },
                                        }}
                                    >
                                        {/* Left Section */}
                                        <Box
                                            sx={{
                                                flex: 1,
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: { xs: pxToRem(4), md: pxToRem(6), lg: pxTovW(6) },
                                            }}
                                        >
                                            <Typography
                                                variant='h5'
                                                sx={{
                                                    fontWeight: 700,
                                                }}
                                            >
                                                {groupName || 'AI Innovators'}
                                            </Typography>

                                            <Typography
                                                variant='subText'
                                                color={'#9F9F9F'}
                                            >
                                                All members will receive the same marks
                                            </Typography>
                                        </Box>

                                        {/* Right Section */}
                                        <Box
                                            sx={{
                                                width: { xs: pxToRem(76), md: pxToRem(52), lg: pxTovW(76) },
                                                height: { xs: pxToRem(56), md: pxToRem(42), lg: pxTovW(60) },
                                                bgcolor: "#FFFFFF",
                                                border: "1px solid #DBDBDB",
                                                borderRadius: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "center",
                                                gap: { xs: pxToRem(5), md: pxToRem(3), lg: pxTovW(3) },
                                                p: { xs: pxToRem(8), md: pxToRem(13), lg: pxTovW(13) },
                                                flexShrink: 0,
                                            }}
                                        >
                                            <InputBase
                                                disabled={!hasMaxMarks}
                                                value={groupMarks}
                                                onChange={handleGroupMarksChange}
                                                placeholder="Enter Marks"
                                                inputProps={{
                                                    style: {
                                                        textAlign: groupMarks ? 'right' : 'left',
                                                    },
                                                }}
                                                sx={{
                                                    color: "#9F9F9F",
                                                    fontWeight: 700,
                                                    fontSize: { md: pxToRem(12), lg: pxTovW(12) },
                                                    width: "100%",
                                                    "& input::placeholder": {
                                                        color: "#9F9F9F",
                                                        opacity: 1,
                                                        fontWeight: 700,
                                                    },
                                                }}
                                            />
                                            <Divider
                                                sx={{
                                                    my: 0.1,
                                                    borderColor: "#E0E0E0",
                                                }}
                                            />
                                            <Typography
                                                variant='subText'
                                                sx={{
                                                    textAlign: "right",
                                                    fontWeight: 700,
                                                }}
                                            >
                                                /{hasMaxMarks ? totalMarks : '–'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                                {!hasMaxMarks && (
                                    <Typography variant='subText' color='error'>
                                        Couldn't load the maximum marks for this project. Close this window and open it again to retry.
                                    </Typography>
                                )}
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                }}>
                                    <Typography variant='bodyText' fontWeight={700}>Resources ({projectContents.length})</Typography>
                                    <Box onClick={individualClick} sx={{
                                        cursor: 'pointer'
                                    }}
                                    ><Typography sx={{
                                        fontSize: { xs: pxToRem(15), md: pxToRem(15), lg: pxTovW(15) },
                                        fontWeight: 400,
                                        color: '#B3B3B3',
                                        textDecoration: 'underline',
                                        textUnderlineOffset: '3px',
                                    }}>Mark Individual</Typography>
                                    </Box>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        width: '100%',
                                        maxWidth: { md: pxToRem(480), lg: pxTovW(537) },
                                        gap: { xs: pxToRem(8), md: pxToRem(8), lg: pxTovW(8) },
                                        flex: 1,
                                        minHeight: 0,
                                        marginY: { md: pxToRem(3), lg: pxTovW(3) },
                                        overflowY: 'auto',
                                        backgroundColor: '#ffffff',
                                        borderRadius: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },

                                        '&::-webkit-scrollbar': {
                                            width: '6px',
                                        },
                                        '&::-webkit-scrollbar-track': {
                                            background: '#FFFFFF',
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            background: '#C5C5C5',
                                            borderRadius: '10px',
                                        },
                                        '&::-webkit-scrollbar-corner': {
                                            background: '#FFFFFF', // Removes the grey corner
                                        },

                                        scrollbarWidth: 'thin',
                                        scrollbarColor: '#C5C5C5 #FFFFFF',
                                    }}
                                >
                                    {projectContents.map((item, index) => (
                                        <Box key={index}>
                                            <ProjectContentCard
                                                key={item.serialNumber}
                                                serialNumber={item.serialNumber}
                                                taskName={item.taskName}
                                                contentName={item.contentName}
                                                duration={item.duration}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                            <Box sx={{
                                height: { xs: pxToRem(50), md: pxToRem(36), lg: pxTovW(50) },
                                width: { xs: '100%', md: pxToRem(480), lg: pxTovW(537) },
                                borderRadius: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
                                backgroundColor: '#EFF4EF',
                                border: '1px solid #DBDBDB',
                                padding: {
                                    xs: `${pxToRem(12)} ${pxToRem(12)} ${pxToRem(12)} ${pxToRem(12)}`,
                                    md: pxToRem(14),
                                    lg: `${pxTovW(13)} ${pxTovW(28)}`
                                },
                                display: 'flex',
                                alignItems: 'center',
                                gap: { xs: pxToRem(10), md: pxToRem(21), lg: pxTovW(24) },
                                boxSizing: 'border-box',
                            }}>

                                <IconWrapper
                                    name="gil-notice"
                                    parentFolder="icons"
                                    type="png"
                                    size="small"
                                    customSx={{
                                        height: {
                                            xs: pxToRem(24),
                                            md: pxToRem(20),
                                            lg: pxTovW(24),
                                        },
                                        width: {
                                            xs: pxToRem(24),
                                            md: pxToRem(20),
                                            lg: pxTovW(24),
                                        },
                                    }}
                                />
                                <Typography variant='h5' fontWeight={'bold'} color={'#B3B3B3'}>Marks entered will be assigned to all {memberCount ?? 4} members</Typography>
                            </Box>
                            <Box sx={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: { xs: 'center', lg: 'flex-end' },
                                gap: { xs: pxToRem(15), md: pxToRem(18), lg: pxTovW(20) },
                            }}>
                                <Button
                                    variant="contained"
                                    onClick={popupCloseHandler}
                                    sx={{
                                        width: {
                                            xs: '50%',
                                            md: pxToRem(150),
                                            lg: pxTovW(200),
                                        },
                                        height: { xs: pxToRem(52), lg: 'unset' },
                                        fontFamily: 'Lato',
                                        fontWeight: 700,
                                        color: "#0AA34F",
                                        border: '1px solid #0AA34F',
                                        fontSize: { xs: pxToRem(16), md: pxToRem(16), lg: pxTovW(16) },
                                        borderRadius: '10px',
                                        backgroundColor: '#FFFFFF',
                                        '&:hover': {
                                            backgroundColor: '#FFFFFF',
                                            color: '#0AA34F',
                                            border: '1px solid #0AA34F',
                                            boxShadow: 'none',
                                        },
                                    }}
                                >Cancel</Button>
                                <PrimaryButton
                                    variant="contained"
                                    onClick={() => proceedButtonData.onClick(Number(groupMarks))}
                                    sx={styles.proceedButton}
                                    disabled={
                                        proceedButtonData.disabled ||
                                        !hasMaxMarks ||
                                        groupMarks === ''
                                    }
                                >
                                    <Typography variant='cardText' fontWeight={700}>Submit Marks</Typography>
                                </PrimaryButton>
                            </Box>
                        </Box>
                        <Button onClick={popupCloseHandler} sx={{ p: '0px' }}>
                            <IconWrapper
                                name="close-yellow"
                                size="md"
                                parentFolder="icons"
                                type="png"
                                customSx={{
                                    width: { xs: pxToRem(33), md: pxToRem(39), lg: pxTovW(54) },
                                    height: { xs: pxToRem(33), md: pxToRem(39), lg: pxTovW(54) },
                                }}
                            />
                        </Button>
                    </Box>
                </Box>
            </Slide>
        </Modal >
    );
};
